import { DeleteForm } from 'components/DeleteForm/DeleteForm';
import { useToast } from 'components/ToastManager';
import { useDeleteMcpClientMutation } from 'hooks/internalApi';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React, { type FC, useCallback } from 'react';
import { getResultOrError } from 'utils/errors';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { Toast } from '@fragment/ui/src/components/Toast';

import type { McpClientNode } from './types';

export type DeleteMcpClientFormProps = {
  mcpClient: McpClientNode;
  onDelete: () => void;
};

export const DeleteMcpClientForm: FC<DeleteMcpClientFormProps> = ({
  mcpClient,
  onDelete,
}) => {
  const workspaceId = useWorkspaceId();
  const { context } = useInternalApiContext();
  const [{ fetching }, deleteClient] = useDeleteMcpClientMutation();
  const { showToast } = useToast();

  const handleDelete = useCallback(async () => {
    const { data, error } = await deleteClient(
      {
        workspaceId,
        clientId: mcpClient.id,
      },
      context
    );
    const { isError } = getResultOrError(data?.deleteMcpClient, error);
    if (isError) {
      showToast(<Toast type="error" message="Error Deleting MCP Client" />);
    } else {
      showToast(<Toast type="success" message="MCP Client Deleted" />);
      onDelete();
    }
  }, [deleteClient, workspaceId, mcpClient.id, context, showToast, onDelete]);

  return (
    <DeleteForm
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- mirrors DeleteClientForm
      onConfirm={handleDelete}
      fetching={fetching}
      fetchingText="Deleting Client"
      confirmText={`Are you sure you want to delete '${mcpClient.name}'? This action cannot be undone.`}
      buttonActionText="Delete"
      confirmButtonText="Delete Client"
    />
  );
};
